import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import i18nInstance from "../i18n/i18n";

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState("Home");
  const [logoIndex, setLogoIndex] = useState(0);
  const [logoFading, setLogoFading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [langDropOpen, setLangDropOpen] = useState(false);
  const [profileDropOpen, setProfileDropOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // 5️⃣ Use Translation Hook — i18n.language is reactive, re-renders on change
  const { t, i18n } = useTranslation();

  // Initialize navigate
  const navigate = useNavigate();

  // Read user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setCurrentUser(JSON.parse(storedUser)); } catch (e) {}
    }
    // Listen for login/logout events from other components
    const handleStorageChange = () => {
      const u = localStorage.getItem('user');
      setCurrentUser(u ? JSON.parse(u) : null);
    };
    window.addEventListener('storage', handleStorageChange);
    // Also listen to a custom event for same-tab login/logout
    window.addEventListener('authChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setShowLogoutConfirm(false);
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };

  // ── HISTORY API / BACK BUTTON EXIT TRAP (`showLogoutConfirm`) ──
  const path = window.location.pathname.toLowerCase();

  useEffect(() => {
    // Only apply trap if user is logged in AND they are exactly on the '/home' page
    if (!currentUser || path !== "/home") return;

    const applyTrap = () => {
      // If we've already trapped this specific history entry, don't double-push
      if (window.history.state && window.history.state.atHome) return; 
      
      // Lay the trap by replacing the current blank state with the trap state
      window.history.replaceState({ isExitTrap: true }, "");
      // Then immediately push the /home state forward so they have a state to pop back from
      window.history.pushState({ atHome: true }, "", window.location.href);
    };

    applyTrap();

    const handlePopState = (e) => {
      // If the user navigates back and hits the trap state
      if (e.state && e.state.isExitTrap === true) {
        // Intercept it: show modal and push the state back forward so they don't exit
        setShowLogoutConfirm(true);
        window.history.pushState({ atHome: true }, "", window.location.href);
      } else {
        setShowLogoutConfirm(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentUser, path]);



  const logos = [
    { type: "img", src: "/logo.png" },
    { type: "symbol", symbol: "ॐ" },
  ];

  // Public links always visible; protected links only when logged in
  const publicNavLinks = [
    { key: "navHome",    path: currentUser ? "/home" : "/" },
    { key: "navAbout",   path: "/about" },
    { key: "navContact", path: "/contact" },
  ];
  const protectedNavLinks = [
    { key: "navBooking",   path: "/booking" },
    { key: "navDonations", path: "/donations" },
    { key: "navReports",   path: "/reports" },
  ];
  const visibleNavLinks = currentUser
    ? [...publicNavLinks, ...protectedNavLinks]
    : publicNavLinks;

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (!e.target.closest('.lang-dropdown-wrap'))    setLangDropOpen(false);
      if (!e.target.closest('.profile-dropdown-wrap')) setProfileDropOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setLogoFading(true);
      setTimeout(() => {
        setLogoIndex(i => (i + 1) % logos.length);
        setLogoFading(false);
      }, 400);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  // 6️⃣ Language Switch Function
  const toggleLang = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');

        :root {
          --crimson-deep:    #1a0404;
          --crimson-mid:     #2c0808;
          --crimson-rich:    #3d0c0c;
          --crimson-accent:  #6b1414;
          --gold-bright:     #e8c46a;
          --gold-mid:        #c9973a;
          --gold-dim:        #8a6020;
          --gold-pale:       #f5e4b0;
          --ivory:           #fdf6e3;
          --glass:           rgba(255,255,255,0.04);
          --glass-border:    rgba(232,196,106,0.18);
          --shadow-deep:     0 20px 60px rgba(0,0,0,0.7);
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        /* ═══════════════════════════════════════════════
           NAVBAR WRAPPER
        ═══════════════════════════════════════════════ */
        .nav-root {
          font-family: 'Cinzel', serif;
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
        }

        /* Top ornamental strip */
        .nav-strip {
          height: 3px;
          background: linear-gradient(90deg,
            transparent 0%,
            var(--gold-dim) 10%,
            var(--gold-bright) 30%,
            #fff8e7 50%,
            var(--gold-bright) 70%,
            var(--gold-dim) 90%,
            transparent 100%
          );
          position: relative;
        }
        .nav-strip::before, .nav-strip::after {
          content: '✦';
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          color: var(--gold-bright);
          font-size: 10px;
        }
        .nav-strip::before { left: 8%; }
        .nav-strip::after  { right: 8%; }

        /* Main bar */
        .nav-bar {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          padding: 0 36px;
          height: 88px;
          background:
            radial-gradient(ellipse at 50% -20%, rgba(109,20,20,0.5) 0%, transparent 60%),
            linear-gradient(180deg, #280808 0%, #1a0404 60%, #120202 100%);
          border-bottom: 1px solid rgba(232,196,106,0.12);
          transition: height 0.35s ease, box-shadow 0.35s ease;
          box-shadow: ${scrolled ? "0 8px 40px rgba(0,0,0,0.8), 0 2px 0 rgba(232,196,106,0.15)" : "var(--shadow-deep)"};
        }

        .nav-bar.scrolled {
          height: 72px;
        }

        /* ═══════════════════════════════════════════════
           LEFT — LOGO CLUSTER
        ═══════════════════════════════════════════════ */
        .nav-left {
          display: flex;
          align-items: center;
          gap: 18px;
          min-width: 0;
          flex: 1;
        }
        
        /* Wrapper for Link to ensure full click area works cleanly */
        .logo-link-wrapper {
            display: flex;
            align-items: center;
            gap: 18px;
            text-decoration: none;
        }

        .logo-frame {
          position: relative;
          width: 60px;
          height: 60px;
          flex-shrink: 0;
        }

        /* Spinning outer ring */
        .logo-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1.5px solid transparent;
          background: conic-gradient(
            from 0deg,
            transparent 0deg,
            var(--gold-bright) 60deg,
            transparent 120deg,
            var(--gold-mid) 180deg,
            transparent 240deg,
            var(--gold-bright) 300deg,
            transparent 360deg
          ) border-box;
          -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: destination-out;
          mask-composite: exclude;
          animation: spinRing 8s linear infinite;
        }

        @keyframes spinRing {
          to { transform: rotate(360deg); }
        }

        .logo-inner {
          position: absolute;
          inset: 6px;
          border-radius: 50%;
          background: radial-gradient(circle, #3d1010 0%, #1a0404 100%);
          border: 1px solid rgba(232,196,106,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 0 15px rgba(0,0,0,0.6), 0 0 20px rgba(232,196,106,0.12);
        }

        /* Logo content: image or symbol */
        .logo-content {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          transition: opacity 0.4s ease, transform 0.4s ease;
        }
        .logo-content.fading {
          opacity: 0;
          transform: scale(0.65) rotate(25deg);
        }
        .logo-om {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 20px;
          background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 2px 6px rgba(232,196,106,0.5));
        }
        .logo-img-el {
          width: 34px;
          height: 34px;
          object-fit: contain;
          filter: drop-shadow(0 2px 8px rgba(232,196,106,0.45));
          border-radius: 50%;
        }

        .temple-info {
          display: flex;
          flex-direction: column;
          gap: 1px;
          min-width: 0;
          overflow: hidden;
        }

        .temple-name {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 15px;
          font-weight: 900;
          letter-spacing: 2px;
          text-transform: uppercase;
          background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 40%, var(--gold-mid) 70%, #b87a28 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .temple-name-2 {
          font-family: 'Cinzel', serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 1.5px;
          background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 40%, var(--gold-mid) 70%, #b87a28 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .temple-location {
          font-family: 'Cinzel', serif;
          font-size: 9.5px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(232,196,106,0.5);
          white-space: nowrap;
          margin-top: 1px;
          display: block !important;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .temple-sub {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 11.5px;
          color: rgba(232,196,106,0.42);
          letter-spacing: 0.5px;
          white-space: nowrap;
        }

        /* ═══════════════════════════════════════════════
           CENTER — NAV LINKS
        ═══════════════════════════════════════════════ */
        .nav-center {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .nav-link {
          position: relative;
          color: rgba(245,228,176,0.75);
          text-decoration: none;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 1.3px;
          text-transform: uppercase;
          padding: 8px 10px;
          cursor: pointer;
          transition: color 0.3s ease;
          border: none;
          background: none;
          font-family: 'Cinzel', serif;
          white-space: nowrap;
        }

        /* Top & bottom micro-borders on hover */
        .nav-link::before, .nav-link::after {
          content: '';
          position: absolute;
          left: 50%;
          transform: translateX(-50%) scaleX(0);
          height: 1px;
          width: 70%;
          background: var(--gold-bright);
          transition: transform 0.35s ease, opacity 0.35s ease;
          opacity: 0;
          box-shadow: 0 0 8px var(--gold-bright);
        }
        .nav-link::before { top: 0; }
        .nav-link::after  { bottom: 0; }

        .nav-link:hover,
        .nav-link.active {
          color: var(--gold-pale);
        }

        .nav-link:hover::before,
        .nav-link:hover::after,
        .nav-link.active::before,
        .nav-link.active::after {
          transform: translateX(-50%) scaleX(1);
          opacity: 1;
        }

        /* Diamond separator */
        .nav-sep {
          width: 4px;
          height: 4px;
          background: var(--gold-dim);
          transform: rotate(45deg);
          flex-shrink: 0;
          opacity: 0.6;
        }

        /* ═══════════════════════════════════════════════
           RIGHT — CONTROLS
        ═══════════════════════════════════════════════ */
        .nav-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 14px;
        }

        /* ═══════════════════════════════════════════════
           LANGUAGE DROPDOWN
        ═══════════════════════════════════════════════ */
        .lang-dropdown-wrap {
          position: relative;
        }
        .lang-trigger {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 7px 14px;
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(232,196,106,0.25);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.25s ease;
          font-family: 'Cinzel', serif;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          color: var(--gold-bright);
          white-space: nowrap;
        }
        .lang-trigger:hover {
          background: rgba(232,196,106,0.1);
          border-color: rgba(232,196,106,0.45);
        }
        .lang-trigger-arrow {
          font-size: 8px;
          opacity: 0.7;
          transition: transform 0.25s ease;
          display: inline-block;
        }
        .lang-trigger-arrow.open { transform: rotate(180deg); }
        .lang-globe {
          font-size: 13px;
          opacity: 0.85;
        }
        .lang-drop-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 150px;
          background: linear-gradient(160deg, #220707 0%, #160303 100%);
          border: 1px solid rgba(232,196,106,0.2);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(232,196,106,0.06);
          opacity: 0;
          transform: translateY(-6px);
          pointer-events: none;
          transition: opacity 0.2s ease, transform 0.2s ease;
          z-index: 2000;
        }
        .lang-drop-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: all;
        }
        .lang-drop-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 16px;
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 1px;
          color: rgba(245,228,176,0.7);
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          text-transform: uppercase;
        }
        .lang-drop-item:hover {
          background: rgba(232,196,106,0.08);
          color: var(--gold-pale);
        }
        .lang-drop-item.active {
          background: rgba(232,196,106,0.1);
          color: var(--gold-bright);
        }
        .lang-drop-item.active::after {
          content: '✦';
          margin-left: auto;
          font-size: 8px;
          color: var(--gold-bright);
        }
        .lang-drop-divider {
          height: 1px;
          background: rgba(232,196,106,0.1);
          margin: 0 12px;
        }
        .lang-flag {
          font-size: 15px;
        }

        /* ═══════════════════════════════════════════════
           AUTH BUTTONS (Login / Register)
        ═══════════════════════════════════════════════ */
        .btn {
          font-family: 'Cinzel', serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 11px 30px;
          transition: all 0.35s ease;
          white-space: nowrap;
          border: none;
          position: relative;
          clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%);
        }

        .btn-ghost {
          background: transparent;
          color: rgba(232,196,106,0.75);
          outline: none;
        }
        /* Ghost border via layered pseudo-element */
        .btn-ghost::before {
          content: '';
          position: absolute;
          inset: 0;
          clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%);
          background: rgba(232,196,106,0.18);
          transition: background 0.35s ease;
          z-index: -1;
        }
        /* Inner fill sweep */
        .btn-ghost::after {
          content: '';
          position: absolute;
          inset: 0;
          clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%);
          background: rgba(232,196,106,0.07);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
          z-index: -1;
        }
        .btn-ghost:hover::after { transform: scaleX(1); }
        .btn-ghost:hover {
          color: var(--gold-pale);
          box-shadow: 0 0 18px rgba(232,196,106,0.12);
        }
        .btn-ghost:hover::before {
          background: rgba(232,196,106,0.28);
        }

        .btn-gold {
          background: linear-gradient(135deg, #8a5a10 0%, var(--gold-bright) 35%, #f7edd0 55%, var(--gold-bright) 75%, #7a4e0c 100%);
          color: #1a0404;
          box-shadow: 0 4px 22px rgba(201,151,58,0.45), inset 0 1px 0 rgba(255,255,255,0.35);
          overflow: hidden;
        }
        /* Shimmer sweep */
        .btn-gold::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -70%;
          width: 45%;
          height: 200%;
          background: rgba(255,255,255,0.28);
          transform: skewX(-18deg);
          transition: left 0.55s ease;
          pointer-events: none;
        }
        .btn-gold:hover::after { left: 130%; }
        .btn-gold:hover {
          box-shadow: 0 6px 30px rgba(232,196,106,0.65), inset 0 1px 0 rgba(255,255,255,0.4);
          transform: translateY(-2px);
          filter: brightness(1.1);
        }

        /* ═══════════════════════════════════════════════
           HAMBURGER
        ═══════════════════════════════════════════════ */
        .hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
          padding: 8px;
          border: 1px solid rgba(232,196,106,0.2);
          border-radius: 4px;
          background: rgba(0,0,0,0.2);
          transition: border-color 0.3s;
        }
        .hamburger:hover { border-color: var(--gold-mid); }

        .ham-line {
          width: 22px;
          height: 1.5px;
          background: var(--gold-bright);
          transition: all 0.3s ease;
          transform-origin: center;
        }
        .hamburger.open .ham-line:nth-child(1) {
          transform: translateY(6.5px) rotate(45deg);
        }
        .hamburger.open .ham-line:nth-child(2) {
          opacity: 0; transform: scaleX(0);
        }
        .hamburger.open .ham-line:nth-child(3) {
          transform: translateY(-6.5px) rotate(-45deg);
        }

        /* ═══════════════════════════════════════════════
           SIDE DRAWER — MOBILE MENU
        ═══════════════════════════════════════════════ */

        /* Backdrop */
        .drawer-backdrop {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0);
          z-index: 1200;
          transition: background 0.4s ease;
          pointer-events: none;
        }
        .drawer-backdrop.open {
          display: block;
          background: rgba(0,0,0,0.7);
          pointer-events: all;
        }

        /* Drawer panel */
        .drawer {
          position: fixed;
          top: 0;
          right: 0;
          width: 300px;
          max-width: 88vw;
          height: 100vh;
          z-index: 1300;
          transform: translateX(100%);
          transition: transform 0.45s cubic-bezier(0.23, 1, 0.32, 1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          /* Angled left edge — unique shape */
          clip-path: polygon(32px 0%, 100% 0%, 100% 100%, 0% 100%);
          background:
            radial-gradient(ellipse at 100% 0%, rgba(100,18,18,0.7) 0%, transparent 55%),
            radial-gradient(ellipse at 100% 100%, rgba(60,10,10,0.5) 0%, transparent 50%),
            linear-gradient(160deg, #200606 0%, #150303 50%, #0e0202 100%);
          border-left: 1px solid rgba(232,196,106,0.12);
        }
        .drawer.open {
          transform: translateX(0);
        }

        /* Gold accent bar on left edge (inside clip) */
        .drawer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 30px;
          width: 1px;
          height: 100%;
          background: linear-gradient(180deg,
            transparent 0%,
            rgba(232,196,106,0.4) 20%,
            rgba(232,196,106,0.6) 50%,
            rgba(232,196,106,0.4) 80%,
            transparent 100%
          );
          pointer-events: none;
        }

        /* Drawer header */
        .drawer-header {
          padding: 28px 28px 20px 44px;
          border-bottom: 1px solid rgba(232,196,106,0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .drawer-title {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          background: linear-gradient(90deg, var(--gold-bright), var(--gold-mid));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .drawer-close {
          width: 34px;
          height: 34px;
          border: 1px solid rgba(232,196,106,0.25);
          background: rgba(0,0,0,0.3);
          color: var(--gold-bright);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          /* Diamond shape */
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
          transition: background 0.3s, transform 0.3s;
          line-height: 1;
        }
        .drawer-close:hover {
          background: rgba(232,196,106,0.15);
          transform: rotate(90deg);
        }

        /* Temple mini-card inside drawer */
        .drawer-temple-card {
          margin: 18px 28px 18px 44px;
          padding: 14px 16px;
          border: 1px solid rgba(232,196,106,0.14);
          background: rgba(232,196,106,0.04);
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
          /* Cut-corner card */
          clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px));
        }
        .drawer-temple-card::after {
          content: 'ॐ';
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 28px;
          color: rgba(232,196,106,0.06);
          font-family: 'Cinzel Decorative', cursive;
          pointer-events: none;
        }
        .drawer-temple-name {
          font-family: 'Cinzel Decorative', cursive;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1px;
          background: linear-gradient(90deg, #fffaf0, var(--gold-bright));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .drawer-temple-loc {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 11px;
          color: rgba(232,196,106,0.45);
          margin-top: 3px;
        }

        /* Nav links in drawer */
        .drawer-nav {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }
        .drawer-nav::-webkit-scrollbar { width: 2px; }
        .drawer-nav::-webkit-scrollbar-track { background: transparent; }
        .drawer-nav::-webkit-scrollbar-thumb { background: rgba(232,196,106,0.3); }

        .drawer-link {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 28px 16px 44px;
          font-family: 'Cinzel', serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(245,228,176,0.65);
          cursor: pointer;
          position: relative;
          transition: all 0.3s ease;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
        }
        /* Numbering */
        .drawer-link-num {
          font-size: 9px;
          font-family: 'Cinzel', serif;
          color: rgba(232,196,106,0.3);
          letter-spacing: 1px;
          min-width: 18px;
          transition: color 0.3s;
        }
        /* Left entry line */
        .drawer-link::before {
          content: '';
          position: absolute;
          left: 31px;
          top: 50%;
          transform: translateY(-50%) scaleY(0);
          width: 2px;
          height: 60%;
          background: var(--gold-bright);
          box-shadow: 0 0 8px var(--gold-bright);
          transition: transform 0.3s ease;
        }
        .drawer-link:hover,
        .drawer-link.active {
          color: var(--gold-pale);
          padding-left: 52px;
          background: rgba(232,196,106,0.04);
        }
        .drawer-link:hover .drawer-link-num,
        .drawer-link.active .drawer-link-num {
          color: var(--gold-bright);
        }
        .drawer-link:hover::before,
        .drawer-link.active::before {
          transform: translateY(-50%) scaleY(1);
        }

        /* Drawer divider */
        .drawer-divider {
          height: 1px;
          margin: 6px 28px 6px 44px;
          background: linear-gradient(90deg, rgba(232,196,106,0.15), transparent);
        }

        /* Drawer footer */
        .drawer-footer {
          padding: 20px 28px 32px 44px;
          border-top: 1px solid rgba(232,196,106,0.1);
          display: flex;
          flex-direction: column;
          gap: 12px;
          flex-shrink: 0;
        }

        .drawer-lang {
          display: flex;
          gap: 0;
          border: 1px solid rgba(232,196,106,0.2);
          border-radius: 6px;
          overflow: hidden;
        }
        .drawer-lang-btn {
          flex: 1;
          background: transparent;
          border: none;
          border-right: 1px solid rgba(232,196,106,0.1);
          padding: 9px 8px;
          font-family: 'Cinzel', serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: rgba(232,196,106,0.45);
          cursor: pointer;
          transition: all 0.25s ease;
          text-align: center;
        }
        .drawer-lang-btn:last-child { border-right: none; }
        .drawer-lang-btn.active {
          background: linear-gradient(135deg, var(--gold-mid) 0%, var(--gold-bright) 50%, var(--gold-mid) 100%);
          color: #1a0404;
        }
        .drawer-lang-btn.active .drawer-lang-flag { filter: sepia(1) saturate(3) hue-rotate(5deg) brightness(0.7); }
        .drawer-lang-btn:hover:not(.active) { color: var(--gold-bright); background: rgba(232,196,106,0.06); }
        .drawer-lang-flag {
          font-size: 14px;
          filter: sepia(1) saturate(4) hue-rotate(5deg) brightness(1.1);
          margin-right: 4px;
        }

        .drawer-btns {
          display: flex;
          gap: 10px;
        }
        .drawer-btns .btn {
          flex: 1;
          text-align: center;
        }
        /* Inside drawer, override clip-path buttons to use border-radius so they don't get clipped */
        .drawer-btns .btn-profile,
        .drawer-btns .btn-logout {
          flex: 1;
          text-align: center;
          clip-path: none !important;
          border-radius: 6px;
          padding: 10px 8px;
        }

        /* Golden tint on flag emojis everywhere */
        .lang-flag,
        .lang-globe {
          filter: sepia(1) saturate(4) hue-rotate(5deg) brightness(1.1);
        }

        /* ═══════════════════════════════════════════════
           BOTTOM ORNAMENTAL STRIP
        ═══════════════════════════════════════════════ */
        .nav-bottom-strip {
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(232,196,106,0.05) 15%,
            rgba(232,196,106,0.2) 40%,
            rgba(232,196,106,0.35) 50%,
            rgba(232,196,106,0.2) 60%,
            rgba(232,196,106,0.05) 85%,
            transparent 100%
          );
        }

        /* ═══════════════════════════════════════════════
           PROFILE BADGE + DROPDOWN
        ═══════════════════════════════════════════════ */
        .profile-dropdown-wrap {
          position: relative;
        }
        .user-badge {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 4px 14px 4px 4px;
          background: linear-gradient(135deg, rgba(20,4,4,0.95) 0%, rgba(35,8,8,0.98) 100%);
          border: 1px solid rgba(232,196,106,0.4);
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0,0,0,0.5), 0 0 0 1px rgba(232,196,106,0.06);
        }
        /* Subtle inner shimmer line at top */
        .user-badge::before {
          content: '';
          position: absolute;
          top: 0; left: 15%; right: 15%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35), transparent);
          pointer-events: none;
        }
        .user-badge:hover {
          border-color: rgba(232,196,106,0.7);
          box-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 0 16px rgba(232,196,106,0.18);
        }
        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-bright) 45%, #f7edd0 65%, var(--gold-mid) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1a0404;
          font-family: 'Cinzel', serif;
          font-size: 14px;
          font-weight: 900;
          flex-shrink: 0;
          box-shadow: 0 0 14px rgba(232,196,106,0.5), 0 2px 8px rgba(0,0,0,0.5);
          border: 1.5px solid rgba(232,196,106,0.5);
          letter-spacing: 0;
        }
        .user-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .user-name {
          font-family: 'Cinzel', serif;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 1.2px;
          background: linear-gradient(90deg, #fffaf0, var(--gold-bright));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          white-space: nowrap;
          max-width: 110px;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 1.2;
        }
        .user-role {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 9px;
          color: rgba(232,196,106,0.55);
          letter-spacing: 0.8px;
          line-height: 1;
        }
        .user-badge-arrow {
          font-size: 7px;
          color: rgba(232,196,106,0.6);
          transition: transform 0.25s ease;
          flex-shrink: 0;
          margin-left: 2px;
        }
        .user-badge-arrow.open { transform: rotate(180deg); }

        /* Profile dropdown panel */
        .profile-drop-menu {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 220px;
          background: linear-gradient(160deg, #220707 0%, #160303 100%);
          border: 1px solid rgba(232,196,106,0.2);
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(232,196,106,0.06);
          opacity: 0;
          transform: translateY(-8px);
          pointer-events: none;
          transition: opacity 0.22s ease, transform 0.22s ease;
          z-index: 2000;
        }
        .profile-drop-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: all;
        }
        /* Arrow pointer */
        .profile-drop-menu::before {
          content: '';
          position: absolute;
          top: -5px;
          right: 22px;
          width: 10px;
          height: 10px;
          background: #220707;
          border-left: 1px solid rgba(232,196,106,0.2);
          border-top: 1px solid rgba(232,196,106,0.2);
          transform: rotate(45deg);
        }
        .profile-drop-header {
          padding: 16px 18px 14px;
          border-bottom: 1px solid rgba(232,196,106,0.1);
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .profile-drop-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8a5a10 0%, var(--gold-bright) 45%, #f7edd0 70%, var(--gold-mid) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1a0404;
          font-family: 'Cinzel', serif;
          font-size: 17px;
          font-weight: 900;
          flex-shrink: 0;
          box-shadow: 0 0 16px rgba(232,196,106,0.35);
        }
        .profile-drop-info {
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 0;
        }
        .profile-drop-name {
          font-family: 'Cinzel', serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          color: var(--gold-pale);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .profile-drop-label {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 10px;
          color: rgba(232,196,106,0.45);
        }
        .profile-drop-body {
          padding: 6px 0;
        }
        .profile-drop-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 18px;
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 1px;
          color: rgba(245,228,176,0.75);
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          text-transform: uppercase;
        }
        .profile-drop-item:hover {
          background: rgba(232,196,106,0.07);
          color: var(--gold-pale);
          padding-left: 22px;
        }
        .profile-drop-item.danger {
          color: rgba(255,140,140,0.75);
        }
        .profile-drop-item.danger:hover {
          background: rgba(139,26,26,0.2);
          color: #ffaaaa;
        }
        .profile-drop-icon {
          font-size: 13px;
          width: 16px;
          text-align: center;
          flex-shrink: 0;
          filter: sepia(1) saturate(5) hue-rotate(5deg) brightness(1.1);
        }
        .profile-drop-item.danger .profile-drop-icon {
          filter: sepia(1) saturate(3) hue-rotate(320deg) brightness(1.1);
        }
        .profile-drop-divider {
          height: 1px;
          background: rgba(232,196,106,0.1);
          margin: 4px 14px;
        }

        /* ═══════════════════════════════════════════════
           PROFILE & LOGOUT BUTTONS (desktop + mobile drawer)
        ═══════════════════════════════════════════════ */
        .btn-profile {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 8px 18px;
          transition: all 0.35s ease;
          white-space: nowrap;
          border: none;
          position: relative;
          background: rgba(232,196,106,0.08);
          color: var(--gold-bright);
          clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
          border: 1px solid rgba(232,196,106,0.3);
        }
        .btn-profile:hover {
          background: rgba(232,196,106,0.18);
          color: var(--gold-pale);
          box-shadow: 0 0 16px rgba(232,196,106,0.2);
        }
        .btn-logout {
          font-family: 'Cinzel', serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          padding: 8px 18px;
          transition: all 0.35s ease;
          white-space: nowrap;
          position: relative;
          background: rgba(139,26,26,0.2);
          color: rgba(255,155,155,0.9);
          clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
          border: 1px solid rgba(200,60,60,0.3);
        }
        .btn-logout:hover {
          background: rgba(139,26,26,0.38);
          color: #ffbbbb;
          box-shadow: 0 0 16px rgba(180,40,40,0.25);
        }

        /* Drawer user card */
        .drawer-user-card {
          margin: 0 8px 0 -6px;
          padding: 12px 14px;
          border: 1px solid rgba(232,196,106,0.2);
          background: rgba(232,196,106,0.06);
          display: flex;
          align-items: center;
          gap: 12px;
          border-radius: 8px;
          flex-shrink: 0;
        }
        .drawer-user-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-bright) 45%, #f7edd0 65%, var(--gold-mid) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1a0404;
          font-family: 'Cinzel', serif;
          font-size: 18px;
          font-weight: 900;
          flex-shrink: 0;
          box-shadow: 0 0 14px rgba(232,196,106,0.45);
          border: 1.5px solid rgba(232,196,106,0.5);
        }
        .drawer-user-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .drawer-user-name {
          font-family: 'Cinzel', serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1px;
          color: var(--gold-pale);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .drawer-user-label {
          font-family: 'EB Garamond', serif;
          font-style: italic;
          font-size: 11px;
          color: rgba(232,196,106,0.5);
        }

        /* ═══════════════════════════════════════════════
           RESPONSIVE
        ═══════════════════════════════════════════════ */
        @media (max-width: 1100px) {
          .nav-link { padding: 10px 12px; letter-spacing: 1px; font-size: 11.5px; }
          .btn { padding: 9px 16px; font-size: 10.5px; }
          .temple-name { font-size: 13px; }
          .temple-name-2 { font-size: 10.5px; }
        }

        @media (max-width: 960px) {
          .temple-name { font-size: 11px; letter-spacing: 1px; }
          .temple-name-2 { font-size: 9.5px; letter-spacing: 0.8px; }
          .temple-location { font-size: 8.5px; letter-spacing: 1px; }
          .logo-frame { width: 52px; height: 52px; }
          .nav-left { gap: 12px; }
        }

        @media (max-width: 860px) {
          .nav-center, .nav-right { display: none; }
          .hamburger { display: flex; }
          .nav-bar { grid-template-columns: 1fr auto; padding: 0 16px; }
          .temple-name { font-size: 12px; letter-spacing: 1px; }
          .temple-name-2 { font-size: 10px; letter-spacing: 0.8px; }
          .temple-location { font-size: 8.5px; letter-spacing: 1px; display: block !important; }
          .logo-frame { width: 50px; height: 50px; }
          .nav-left { gap: 10px; }
        }

        @media (max-width: 500px) {
          .temple-name { font-size: 10.5px; letter-spacing: 0.8px; }
          .temple-name-2 { font-size: 8.5px; letter-spacing: 0.3px; }
          .temple-location { font-size: 8px; letter-spacing: 0.8px; display: block !important; }
          .logo-frame { width: 44px; height: 44px; }
          .logo-om { font-size: 15px; }
          .nav-left { gap: 8px; }
          .drawer { width: 280px; }
          .nav-bar { padding: 0 12px; }
        }

        @media (max-width: 380px) {
          .temple-name { font-size: 9.5px; letter-spacing: 0.5px; }
          .temple-name-2 { font-size: 8px; letter-spacing: 0.2px; }
          .temple-location { font-size: 7.5px; letter-spacing: 0.5px; display: block !important; }
          .logo-frame { width: 38px; height: 38px; }
          .nav-bar { height: 76px; }
        }
        
        /* ═══════════════════════════════════════════════
           LOGOUT CONFIRM MODAL CSS
        ═══════════════════════════════════════════════ */
        .nav-logout-overlay {
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(10,2,2,0.72); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: overlayIn 0.2s ease;
        }
        .nav-logout-modal {
          position: relative; width: 100%; max-width: 380px;
          background: radial-gradient(ellipse at 50% 0%, rgba(90,18,18,0.98) 0%, rgba(40,8,8,0.98) 50%, #0e0101 100%);
          border: 1px solid rgba(232,196,106,0.25);
          clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px));
          box-shadow: 0 32px 90px rgba(0,0,0,0.85), inset 0 0 40px rgba(0,0,0,0.5);
          animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both;
          overflow: hidden; text-align: center; padding-bottom: 6px;
        }
        @keyframes regalIn { from { opacity:0; transform: scale(0.92) translateY(16px); } to { opacity:1; transform: scale(1) translateY(0); } }
        .lm-topstrip {
          height: 3px;
          background: linear-gradient(90deg, transparent, rgba(232,196,106,0.5), #e8c46a, rgba(232,196,106,0.5), transparent);
          margin-bottom: 28px;
        }
        .lm-corner {
          position: absolute; width: 10px; height: 10px;
          border-color: rgba(232,196,106,0.5); border-style: solid;
        }
        .lm-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
        .lm-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
        .lm-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
        .lm-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
        .lm-icon-wrap {
          position: relative; width: 68px; height: 68px; border-radius: 50%;
          background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.3);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 12px;
          box-shadow: inset 0 0 15px rgba(0,0,0,0.6);
        }
        .lm-icon-ring {
          position: absolute; inset: -5px; border-radius: 50%;
          border: 1px dashed rgba(232,196,106,0.5);
          animation: spinRing 8s linear infinite;
        }
        .lm-icon-ring2 { inset: -12px; animation-duration: 14s; animation-direction: reverse; border-color: rgba(232,196,106,0.3); }
        .lm-icon { font-size: 26px; color: #e8c46a; position: relative; z-index: 1; display: flex; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); }
        .lm-eyebrow {
          font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 4px;
          color: #c9973a; text-transform: uppercase; margin-bottom: 6px;
        }
        .lm-title {
          font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900;
          background: linear-gradient(135deg, #fffaf0 0%, #e8c46a 35%, #c9973a 65%, #c49028 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          margin-bottom: 2px;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
          letter-spacing: 1.5px;
        }
        .lm-divider {
          height: 1px; margin: 14px 36px;
          background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35), transparent);
          display: flex; align-items: center; justify-content: center; position: relative;
        }
        .lm-gem {
          width: 6px; height: 6px; transform: rotate(45deg);
          background: rgba(232,196,106,0.6); position: absolute;
        }
        .lm-message {
          font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px;
          color: rgba(245,228,176,0.7); line-height: 1.7;
          padding: 0 32px 20px;
          letter-spacing: 1px;
        }
        .lm-actions {
          display: flex; gap: 10px; justify-content: center;
          flex-wrap: wrap; padding: 0 28px 20px;
        }
        .lm-btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 12px 30px; font-family: 'Cinzel', serif; font-size: 11px;
          font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
          cursor: pointer; transition: all 0.35s ease; border: none;
          clip-path: polygon(12px 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0 50%);
        }
        .lm-cancel {
          background: transparent; color: rgba(232,196,106,0.8);
          border: 1px solid rgba(232,196,106,0.3);
        }
        .lm-cancel:hover { background: rgba(232,196,106,0.08); color: #fdf6e3; border: 1px solid #c9973a; }
        .lm-confirm {
          background: linear-gradient(135deg, #8a5a10 0%, #e8c46a 35%, #f7edd0 55%, #e8c46a 75%, #7a4e0c 100%);
          color: #1a0404; box-shadow: 0 4px 15px rgba(201,151,58,0.4);
        }
        .lm-confirm:hover { filter: brightness(1.15); transform: translateY(-2px); box-shadow: 0 6px 20px rgba(201,151,58,0.55); }
        .lm-bottomstrip {
          height: 4px; margin-top: 14px;
          background: linear-gradient(90deg, transparent, rgba(201,151,58,0.3), #e8c46a, rgba(201,151,58,0.3), transparent);
        }

        /* Modal Responsive */
        @media (max-width: 768px) {
          .nav-logout-modal { 
            max-width: 100vw; width: 100%; margin: 0;
            border-radius: 24px 24px 0 0; clip-path: none;
            position: fixed; bottom: 0; left: 0; right: 0;
            animation: slideUpModal 0.4s cubic-bezier(0.25, 1, 0.5, 1) both;
          }
          .nav-logout-overlay { align-items: flex-end; padding: 0; }
          @keyframes slideUpModal { from { transform: translateY(100%); } to { transform: translateY(0); } }
          
          .lm-topstrip { height: 4px; margin-bottom: 24px; }
          .lm-title { font-size: 20px; }
          .lm-message { font-size: 15px; padding: 0 24px 20px; color: rgba(245,228,176,0.85); }
          .lm-actions { flex-direction: column; padding: 0 24px 28px; gap: 10px; }
          .lm-btn { width: 100%; justify-content: center; clip-path: none; border-radius: 30px; padding: 14px; }
          .lm-icon-wrap { width: 60px; height: 60px; margin-bottom: 10px; }
          .lm-icon { font-size: 24px; }
          .lm-corner { display: none; }
        }
      `}</style>

      <div className="nav-root">
        {/* Top shimmer strip */}
        <div className="nav-strip" />

        {/* Main navbar */}
        <nav className={`nav-bar${scrolled ? " scrolled" : ""}`}>

          {/* LEFT — Logo + Name */}
          <div className="nav-left">
            {/* Link wraps the logo area for routing to Home (/) */}
            <Link to={currentUser ? "/home" : "/"} className="logo-link-wrapper">
              <div className="logo-frame">
                <div className="logo-ring" />
                <div className="logo-inner">
                  <div className={`logo-content${logoFading ? " fading" : ""}`}>
                    {logos[logoIndex].type === "img" ? (
                      <img
                        src={logos[logoIndex].src}
                        alt="Temple Logo"
                        className="logo-img-el"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <span className="logo-om">{logos[logoIndex].symbol}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="temple-info">
                {/* 7️⃣ Use Translation */}
                <span className="temple-name">{t("templeName1")}</span>
                <span className="temple-name temple-name-2">{t("templeSub")}</span>
                <span className="temple-location">✦ {t("templeLoc")} ✦</span>
              </div>
            </Link>
          </div>

          {/* CENTER — Links */}
          <div className="nav-center">
            {visibleNavLinks.map(({ key, path }, i) => (
              <>
                <Link
                  key={key}
                  to={path}
                  className={`nav-link${activeLink === t(key) ? " active" : ""}`}
                  onClick={() => setActiveLink(t(key))}
                >
                  {t(key)}
                </Link>
                {i < visibleNavLinks.length - 1 && <span className="nav-sep" key={`sep-${i}`} />}
              </>
            ))}
          </div>

          {/* RIGHT — Language Dropdown + Auth */}
          <div className="nav-right">

            {/* Language Dropdown */}
            <div className="lang-dropdown-wrap">
              <button
                className="lang-trigger"
                onClick={() => { setLangDropOpen(o => !o); setProfileDropOpen(false); }}
              >
                <span className="lang-globe">🌐</span>
                {i18n.language === "ml" ? "മലയാളം" : "English"}
                <span className={`lang-trigger-arrow${langDropOpen ? " open" : ""}`}>▼</span>
              </button>
              <div className={`lang-drop-menu${langDropOpen ? " open" : ""}`}>
                <button
                  className={`lang-drop-item${i18n.language === "en" ? " active" : ""}`}
                  onClick={() => { toggleLang("en"); setLangDropOpen(false); }}
                >
                  <span className="lang-flag">🇬🇧</span> English
                </button>
                <div className="lang-drop-divider" />
                <button
                  className={`lang-drop-item${i18n.language === "ml" ? " active" : ""}`}
                  onClick={() => { toggleLang("ml"); setLangDropOpen(false); }}
                >
                  <span className="lang-flag">🇮🇳</span> മലയാളം
                </button>
              </div>
            </div>

            {currentUser ? (
              /* ── Logged-in: profile badge with dropdown ── */
              <div className="profile-dropdown-wrap">
                <div
                  className="user-badge"
                  onClick={() => { setProfileDropOpen(o => !o); setLangDropOpen(false); }}
                >
                  <div className="user-avatar">
                    {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="user-info">
                    <span className="user-name">{currentUser.name || "Devotee"}</span>
                    <span className="user-role">✦ Devotee</span>
                  </div>
                  <span className={`user-badge-arrow${profileDropOpen ? " open" : ""}`}>▼</span>
                </div>

                <div className={`profile-drop-menu${profileDropOpen ? " open" : ""}`}>
                  {/* Header with full info */}
                  <div className="profile-drop-header">
                    <div className="profile-drop-avatar">
                      {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className="profile-drop-info">
                      <span className="profile-drop-name">{currentUser.name || "Devotee"}</span>
                      <span className="profile-drop-label">✦ Signed In</span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="profile-drop-body">
                    <button className="profile-drop-item" onClick={() => { setProfileDropOpen(false); navigate('/profile'); }}>
                      <span className="profile-drop-icon">👤</span> My Profile
                    </button>
                    <button className="profile-drop-item" onClick={() => { setProfileDropOpen(false); navigate('/Mybooking'); }}>
                      <span className="profile-drop-icon">📋</span> My Bookings
                    </button>
                    <div className="profile-drop-divider" />
                    <button className="profile-drop-item danger" onClick={() => { setProfileDropOpen(false); handleLogout(); }}>
                      <span className="profile-drop-icon">🚪</span> Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* ── Logged-out: login / register buttons ── */
              <>
                <button className="btn btn-ghost" onClick={() => navigate('/login')}>{t("navLogin")}</button>
                <button className="btn btn-gold" onClick={() => navigate('/register')}>{t("navRegister")}</button>
              </>
            )}
          </div>

          {/* Hamburger */}
          <div
            className={`hamburger${isMobileMenuOpen ? " open" : ""}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="ham-line" />
            <div className="ham-line" />
            <div className="ham-line" />
          </div>
        </nav>

        {/* Bottom accent line */}
        <div className="nav-bottom-strip" />

        {/* Backdrop */}
        <div
          className={`drawer-backdrop${isMobileMenuOpen ? " open" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* Side Drawer */}
        <div className={`drawer${isMobileMenuOpen ? " open" : ""}`}>

          {/* Drawer Header */}
          <div className="drawer-header">
            <span className="drawer-title">Navigation</span>
            <button className="drawer-close" onClick={() => setIsMobileMenuOpen(false)}>✕</button>
          </div>

          {/* Temple Mini-Card */}
          <div className="drawer-temple-card">
            <div className="drawer-temple-name">{t("templeName")}</div>
            <div className="drawer-temple-name" style={{ marginTop: "2px" }}>{t("templeSub")}</div>
            <div className="drawer-temple-loc">{t("templeLoc")}</div>
          </div>

          {/* Nav Links */}
          <nav className="drawer-nav">
            {visibleNavLinks.map(({ key, path }, i) => (
              <div key={key}>
                <button
                  className={`drawer-link${activeLink === t(key) ? " active" : ""}`}
                  onClick={() => {
                    setActiveLink(t(key));
                    setIsMobileMenuOpen(false);
                    navigate(path);
                  }}
                >
                  <span className="drawer-link-num">0{i + 1}</span>
                  {t(key)}
                </button>
                {i < visibleNavLinks.length - 1 && <div className="drawer-divider" />}
              </div>
            ))}
          </nav>

          {/* Drawer Footer */}
          <div className="drawer-footer">
            <div className="drawer-lang">
              {["en", "ml"].map((l) => (
                <button
                  key={l}
                  className={`drawer-lang-btn${i18n.language === l ? " active" : ""}`}
                  onClick={() => toggleLang(l)}
                >
                  <span className="drawer-lang-flag">{l === "en" ? "🇬🇧" : "🇮🇳"}</span>
                  {l === "en" ? "English" : "മലയാളം"}
                </button>
              ))}
            </div>

            {currentUser ? (
              /* ── Logged-in: show user card + profile/logout ── */
              <>
                <div className="drawer-user-card">
                  <div className="drawer-user-avatar">
                    {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="drawer-user-info">
                    <span className="drawer-user-name">{currentUser.name || t("devotee")}</span>
                    <span className="drawer-user-label">{ t("signedIn") }</span>
                  </div>
                </div>
                <div className="drawer-btns">
                  <button className="btn-profile" onClick={() => { setIsMobileMenuOpen(false); navigate('/profile'); }}>
                    {t("profile")}
                  </button>
                  <button className="btn-logout" onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}>
                    {t("logout")}
                  </button>
                </div>
              </>
            ) : (
              /* ── Logged-out: show login/register ── */
              <div className="drawer-btns">
                <button className="btn btn-ghost" onClick={() => { setIsMobileMenuOpen(false); navigate('/login'); }}>{t("navLogin")}</button>
                <button className="btn btn-gold" onClick={() => { setIsMobileMenuOpen(false); navigate('/register'); }}>{t("navRegister")}</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── LOGOUT CONFIRM MODAL ── */}
      {showLogoutConfirm && (
        <div className="nav-logout-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="nav-logout-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-topstrip" />
            <span className="lm-corner tl" /><span className="lm-corner tr" />
            <span className="lm-corner bl" /><span className="lm-corner br" />
            <div className="lm-icon-wrap">
              <div className="lm-icon-ring" />
              <div className="lm-icon-ring lm-icon-ring2" />
              <span className="lm-icon"><FaTimes /></span>
            </div>
            <div className="lm-eyebrow">✦ {t("templeTitle") || "Kavil Sree Bhadrakali"} ✦</div>
            <h3 className="lm-title">{t("logout") || "Logout"}</h3>
            <div className="lm-divider"><span className="lm-gem" /></div>
            <p className="lm-message">
              {i18nInstance.language === "ml"
                ? "നിങ്ങൾ അക്കൗണ്ടിൽ നിന്ന് ലോഗൗട്ട് ചെയ്യാൻ ആഗ്രഹിക്കുന്നുണ്ടോ?"
                : "Are you sure you want to logout?"}
            </p>
            <div className="lm-actions">
              <button className="lm-btn lm-cancel" onClick={() => setShowLogoutConfirm(false)}>
                {t("btnCancel") || "Cancel"}
              </button>
              <button className="lm-btn lm-confirm" onClick={handleLogout}>
                {t("logout") || "Logout"}
              </button>
            </div>
            <div className="lm-bottomstrip" />
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;